/**
 * The organization's network policy (`org/policy.ts` in contracts), under the
 * Network page's rows.
 *
 * Not rows, and not locks: a row is a value a member's machine starts from or
 * is held to, and these are lists that hold over **every workspace on every
 * member's machine** — the organization's own copies and each member's
 * personal ones — with nothing beneath them to inherit or override. So there is
 * no padlock to press; being on the list is the decision.
 *
 * The two lists are refused by the same validator the Worker uses, entry by
 * entry, so a refusal is said under the field that caused it.
 */
import type React from 'react';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import { AGENTS, type AgentId } from '@airlock/contracts/settings/agents';
import { modelsFor } from '@airlock/contracts/settings/models';
import { color } from '../../theme/tokens';
import { SettingSet } from '../../components/settings-row';
import type { OrgSettings } from '../../lib/types';
import { DomainList, ListBlock } from './list-settings';

export function NetworkPolicy({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  const put = (egress: Partial<OrgPolicy['egress']>): void =>
    onChange({ ...EMPTY_ORG_POLICY, ...policy, egress: { ...policy.egress, ...egress } });
  return (
    <>
      <ListBlock
        label="Allowed on every machine"
        hint="Added to the allowlist of every workspace on every member's machine — their own included. Use it for the hosts your work always needs, so nobody is asked about them."
      >
        <DomainList
          value={policy.egress.allowedDomains}
          onChange={(allowedDomains) => put({ allowedDomains })}
          editable={editable}
          placeholder="artifacts.example.com"
        />
      </ListBlock>
      <ListBlock
        label="Blocked on every machine"
        hint="Refused everywhere, with every name under it: taken off any list a member or a workspace wrote, never put to anybody as a question, and refused if somebody tries to allow it. Airlock's own hosts — the agents' endpoints and the package registries — stay open, since no session runs without them."
      >
        <DomainList
          value={policy.egress.blockedDomains}
          onChange={(blockedDomains) => put({ blockedDomains })}
          editable={editable}
          placeholder="pastebin.com"
        />
      </ListBlock>
    </>
  );
}

/**
 * Each offered agent's models a run may name (`policy.models`), under the All
 * agents page's rows. Nothing chosen is the answer "any of its own" — the list
 * narrows, it never has to be filled in. Which agents may run at all is the
 * *Agents a member may launch* row above it, locked.
 */
export function ModelsPolicy({
  settings,
  offered,
  editable,
  onChange
}: {
  settings: OrgSettings;
  /** The agents this organization offers — an agent it does not has nothing to narrow. */
  offered: readonly AgentId[];
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  const put = (agent: AgentId, models: string[]): void => {
    const next = { ...policy.models };
    if (models.length) next[agent] = models;
    else delete next[agent];
    onChange({ ...EMPTY_ORG_POLICY, ...policy, models: next });
  };
  return (
    <>
      {AGENTS.filter((agent) => offered.includes(agent.value)).map((agent) => {
        const chosen = policy.models[agent.value] ?? [];
        const options = modelsFor(agent.value).map((one) => ({ value: one.value, label: one.label }));
        return (
          <ListBlock
            key={agent.value}
            label={`${agent.label} models a run may use`}
            hint="On every member's machine: a task, a workflow step or an agent naming another model is refused before it starts, and the pickers leave it out. None chosen is any of its models. The default model has to be one of them."
          >
            {editable ? (
              <SettingSet value={chosen} options={options} onChange={(next) => put(agent.value, next)} allowEmpty />
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>
                {chosen.length
                  ? options
                      .filter((one) => chosen.includes(one.value))
                      .map((one) => one.label)
                      .join(', ')
                  : `Any of ${agent.label}’s models.`}
              </p>
            )}
          </ListBlock>
        );
      })}
    </>
  );
}
